import React from "react";
import { BadgeChip } from "../../../../components/OrgShell/BadgeChip";
import { FieldGroup } from "../../../../components/OrgShell/FieldGroup";
import { EmailPreferenceForm } from "../email-preference-editor.component";

interface NotificationsSectionProps {
  form: EmailPreferenceForm;
  onChange: (form: EmailPreferenceForm) => void;
  variant: "vendor" | "customer";
}

/**
 * Honest presentation of the five emailPreference flags:
 * - "New order placed"            → EmailPreferenceNewOrder
 * - "Order needs your approval"   → EmailPreferencePendingOrder
 * - "Design & logo approval requests" → EmailPreferenceNewDesign AND
 *   EmailPreferenceNewLogos written together as ONE toggle
 * - "Order shipped"               → EmailPreferenceOrder, rendered disabled
 *   (COMING LATER) but still round-tripped unchanged on save
 * All five fields keep flowing through the existing profile save body.
 */
export function NotificationsSection({
  form,
  onChange,
  variant,
}: NotificationsSectionProps) {
  const approvalRequestsChecked =
    form.emailPreferenceNewDesign || form.emailPreferenceNewLogos;

  return (
    <div className="space-y-8">
      <FieldGroup
        title="Email notifications"
        description={
          variant === "vendor"
            ? "These switches control real emails to your primary contact."
            : "These switches control real emails to your contact address."
        }
      >
        <div className="divide-y rounded-md border">
          {variant === "vendor" && (
            <>
              <NotificationRow
                title="New order placed"
                description="Sent when one of your customers places an order."
                badge={<BadgeChip tone="green">NEW</BadgeChip>}
                checked={form.emailPreferenceNewOrder}
                onChange={(next) =>
                  onChange({ ...form, emailPreferenceNewOrder: next })
                }
              />
              <NotificationRow
                title="Order needs your approval"
                description="Sent when an order is waiting for your approval before production."
                checked={form.emailPreferencePendingOrder}
                onChange={(next) =>
                  onChange({ ...form, emailPreferencePendingOrder: next })
                }
              />
            </>
          )}
          <NotificationRow
            title={
              variant === "vendor"
                ? "Design & logo approval requests"
                : "Approval requests"
            }
            description={
              variant === "vendor"
                ? "Sent when a new design or logo is waiting for your approval."
                : "Sent when a design or logo is waiting for your approval."
            }
            checked={approvalRequestsChecked}
            onChange={(next) =>
              onChange({
                ...form,
                emailPreferenceNewDesign: next,
                emailPreferenceNewLogos: next,
              })
            }
          />
          <NotificationRow
            title="Order shipped"
            description="Sent when an order has been shipped."
            badge={<BadgeChip tone="gray">COMING LATER</BadgeChip>}
            checked={form.emailPreferenceOrder}
            disabled
          />
        </div>
      </FieldGroup>
    </div>
  );
}

function NotificationRow({
  title,
  description,
  badge,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  description: string;
  badge?: React.ReactNode;
  checked: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-3 p-4 ${
        disabled ? "opacity-60" : "cursor-pointer hover:bg-gray-50"
      }`}
    >
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">{title}</span>
          {badge}
        </span>
        <span className="mt-0.5 block text-xs text-gray-500">
          {description}
        </span>
      </span>
    </label>
  );
}
